import { ErrorHandler, NgModule, inject, isDevMode } from '@angular/core'
import { providePrimeNG } from 'primeng/config'
import Aura from '@primeuix/themes/aura'
import { BrowserModule } from '@angular/platform-browser'
import { AppRoutingModule } from './app-routing.module'
import { AppComponent } from './app.component'

import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { BrowserAnimationsModule } from '@angular/platform-browser/animations'

import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi, withFetch, HttpHeaders } from '@angular/common/http'

import { NgxSpinnerModule } from 'ngx-spinner'
import { ToastrModule } from 'ngx-toastr'
import { TranslateModule } from '@ngx-translate/core'
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader'
import { ConfirmationService } from 'primeng/api'
import { StatisticService } from './modules/culture/dashboard/services/statistic.service'
import { AuthModule } from './core/auth/auth.module'
import { AuthInterceptorService } from './core/auth/services/auth-interceptor.service'
import { AuthService } from './core/auth/services/auth.service'
import { provideApollo } from 'apollo-angular'
import { HttpLink } from 'apollo-angular/http'
import { InMemoryCache, ApolloLink } from '@apollo/client/core'
import { setContext } from '@apollo/client/link/context'
import { environment } from 'src/config/environments/environment'
import { I18N_VERSION } from 'src/config/i18n-version'
import { ApiService } from './core/graphql/services/api.service'
import { UsersService } from './shared/pages/settings/services/users.service'
import { ToastModule } from 'primeng/toast'

/* Google maps */
import { GoogleMapsModule } from '@angular/google-maps'
import { GlobalErrorHandler } from './core/global-error-handler'
import { ServiceWorkerModule } from '@angular/service-worker'
import { PassUploadErrorsComponent } from './shared/components/pass-upload-errors/pass-upload-errors.component'

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    FormsModule,
    ReactiveFormsModule,
    BrowserAnimationsModule,
    NgxSpinnerModule,
    ToastrModule.forRoot(),
    TranslateModule.forRoot(),
    AuthModule,
    ToastModule,
    GoogleMapsModule,
    // YB-1669 staged pass-upload errors dialog (standalone), mounted once in app.component.html
    PassUploadErrorsComponent,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      // Register the ServiceWorker as soon as the application is stable
      // or after 30 seconds (whichever comes first).
      registrationStrategy: 'registerWhenStable:30000',
    }),
  ],
  providers: [
    StatisticService,
    AuthService,
    ConfirmationService,
    ApiService,
    UsersService,
    provideApollo(() => {
      const httpLink = inject(HttpLink)

      const auth = setContext((_operation, _context) => {
        const token = localStorage.getItem('JWT_STR')
        if (!token) {
          return {}
        }
        return {
          headers: new HttpHeaders({
            Authorization: `Bearer ${token}`,
          }),
        }
      })

      const uri = environment.serverUrl + '/graphql'
      const link = ApolloLink.from([auth, httpLink.create({ uri })])
      const cache = new InMemoryCache()

      return {
        link,
        cache,
      }
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptorService,
      multi: true,
    },
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    // ?v=<content hash> — without it the browser keeps a cached he.json for an hour after a deploy
    // while the new (hash-busted) bundle is already live, so keys added in that release render raw.
    provideTranslateHttpLoader({ prefix: '/assets/i18n/', suffix: `.json?v=${I18N_VERSION}` }),
    { provide: ErrorHandler, useClass: GlobalErrorHandler },
    providePrimeNG({
      theme: {
        preset: Aura,
        options: {
          darkModeSelector: '.dark-mode',
          cssLayer: { name: 'primeng', order: 'primeng, app' },
        },
      },
      zIndex: {
        modal: 1100,
        overlay: 1200,  // must be above modal (1100) so selects in dialogs render on top
        menu: 1200,
        tooltip: 1300,
      },
    }),
  ],
})
export class AppModule {}
